import { 
  WorkflowListResponse,
  WorkflowDetailResponse,
  CreateWorkflowRequest,
  UpdateWorkflowRequest,
  ExecuteWorkflowRequest,
  WorkflowExecutionResponse,
  WorkflowExecutionListResponse,
  WorkflowExecutionDetailResponse,
  TemporalWorkflowExecutionResponse,
  PagedResult,
  WorkflowSummaryDto,
  WorkflowApproverReportDto,
  UserWorkflowAssociationDto
} from '../types';
import { HttpClient } from './http-client';

export class DivisionWorkflowService {
  constructor(private httpClient: HttpClient) {}

  /**
   * Get all workflows for a specific division
   */
  async getWorkflows(
    divisionId: string,
    params?: {
      active?: boolean;
      type?: string;
      matchingMode?: string;
      search?: string;
      sortBy?: string;
      sortOrder?: 'asc' | 'desc';
      page?: number;
      limit?: number;
    }
  ): Promise<PagedResult<WorkflowSummaryDto>> {
    const searchParams = new URLSearchParams();
    if (params?.active !== undefined) {
      searchParams.append('active', params.active.toString());
    }
    if (params?.type) {
      searchParams.append('type', params.type);
    }
    if (params?.matchingMode) {
      searchParams.append('matchingMode', params.matchingMode);
    }
    if (params?.search) {
      searchParams.append('search', params.search);
    }
    if (params?.sortBy) {
      searchParams.append('sortBy', params.sortBy);
    }
    if (params?.sortOrder) {
      searchParams.append('sortOrder', params.sortOrder);
    }
    if (params?.page !== undefined) {
      searchParams.append('page', params.page.toString());
    }
    if (params?.limit !== undefined) {
      searchParams.append('limit', params.limit.toString());
    }

    const query = searchParams.toString();
    const url = `/api/divisions/${divisionId}/workflows${query ? `?${query}` : ''}`;
    
    return this.httpClient.get<PagedResult<WorkflowSummaryDto>>(url);
  }

  /**
   * Get a specific workflow by ID within a division
   */
  async getWorkflow(divisionId: string, workflowId: string): Promise<WorkflowDetailResponse> {
    return this.httpClient.get<WorkflowDetailResponse>(
      `/api/divisions/${divisionId}/workflows/${workflowId}`
    );
  }

  /**
   * Create a new workflow in a division
   */
  async createWorkflow(
    divisionId: string, 
    request: CreateWorkflowRequest
  ): Promise<WorkflowDetailResponse> {
    return this.httpClient.post<WorkflowDetailResponse>(
      `/api/divisions/${divisionId}/workflows`,
      request
    );
  }

  /**
   * Update an existing workflow in a division
   */
  async updateWorkflow(
    divisionId: string,
    workflowId: string,
    request: UpdateWorkflowRequest
  ): Promise<WorkflowDetailResponse> {
    return this.httpClient.put<WorkflowDetailResponse>(
      `/api/divisions/${divisionId}/workflows/${workflowId}`,
      request
    );
  }

  /**
   * Delete a workflow from a division
   */
  async deleteWorkflow(divisionId: string, workflowId: string): Promise<void> {
    return this.httpClient.delete(`/api/divisions/${divisionId}/workflows/${workflowId}`);
  }

  /**
   * Clone an existing workflow in a division
   */
  async cloneWorkflow(
    divisionId: string,
    workflowId: string,
    request?: {
      name?: string;
      active?: boolean;
      includeMatchCriteria?: boolean;
    }
  ): Promise<WorkflowDetailResponse> {
    return this.httpClient.post<WorkflowDetailResponse>(
      `/api/divisions/${divisionId}/workflows/${workflowId}/clone`,
      request || {}
    );
  }

  /**
   * Execute a workflow in a division
   */
  async executeWorkflow(
    divisionId: string,
    workflowId: string,
    request: ExecuteWorkflowRequest
  ): Promise<WorkflowExecutionResponse> {
    return this.httpClient.post<WorkflowExecutionResponse>(
      `/api/divisions/${divisionId}/workflows/${workflowId}/execute`,
      request
    );
  }

  /**
   * Execute a workflow using Temporal orchestration
   */
  async executeTemporalWorkflow(
    divisionId: string,
    workflowId: string,
    request: ExecuteWorkflowRequest
  ): Promise<TemporalWorkflowExecutionResponse> {
    return this.httpClient.post<TemporalWorkflowExecutionResponse>(
      `/api/divisions/${divisionId}/workflows/${workflowId}/execute-temporal`,
      request
    );
  }

  /**
   * Get workflow executions for a specific workflow in a division
   */
  async getWorkflowExecutions(
    divisionId: string,
    workflowId: string,
    params?: {
      status?: string;
      startDate?: string;
      endDate?: string;
      limit?: number;
      offset?: number;
    }
  ): Promise<WorkflowExecutionListResponse> {
    const searchParams = new URLSearchParams();
    if (params?.status) {
      searchParams.append('status', params.status);
    }
    if (params?.startDate) {
      searchParams.append('startDate', params.startDate);
    }
    if (params?.endDate) {
      searchParams.append('endDate', params.endDate);
    }
    if (params?.limit) {
      searchParams.append('limit', params.limit.toString());
    }
    if (params?.offset) {
      searchParams.append('offset', params.offset.toString());
    }

    const query = searchParams.toString();
    const url = `/api/divisions/${divisionId}/workflows/${workflowId}/executions${query ? `?${query}` : ''}`;
    
    return this.httpClient.get<WorkflowExecutionListResponse>(url);
  }

  /**
   * Get a specific workflow execution detail
   */
  async getWorkflowExecution(
    divisionId: string,
    workflowId: string,
    executionId: string
  ): Promise<WorkflowExecutionDetailResponse> {
    return this.httpClient.get<WorkflowExecutionDetailResponse>(
      `/api/divisions/${divisionId}/workflows/${workflowId}/executions/${executionId}`
    );
  }

  /**
   * Get the distinct workflow `type` values actually in use for a division. There's no
   * canonical/enforced list for this free-text field, so this reflects real data rather than
   * any single UI form's hardcoded suggestions.
   */
  async getWorkflowTypes(divisionId: string): Promise<string[]> {
    return this.httpClient.get<string[]>(`/api/divisions/${divisionId}/workflows/types`);
  }

  /**
   * Admin report: approvers per step across every workflow routed on the given JSONPath field,
   * one row per routing-rule value.
   */
  async getApproverReport(
    divisionId: string,
    jsonPath: string,
    filters?: { type?: string; nameContains?: string }
  ): Promise<WorkflowApproverReportDto> {
    const searchParams = new URLSearchParams({ jsonPath });
    if (filters?.type) {
      searchParams.append('type', filters.type);
    }
    if (filters?.nameContains) {
      searchParams.append('nameContains', filters.nameContains);
    }
    return this.httpClient.get<WorkflowApproverReportDto>(
      `/api/divisions/${divisionId}/workflows/routing-rule-report?${searchParams.toString()}`
    );
  }

  /**
   * List every workflow in this division where the given user is a configured (potential)
   * approver on at least one step.
   */
  async getWorkflowsForUser(divisionId: string, userId: string): Promise<UserWorkflowAssociationDto[]> {
    return this.httpClient.get<UserWorkflowAssociationDto[]>(
      `/api/divisions/${divisionId}/workflows/by-user/${userId}`
    );
  }

  /**
   * Import workflows from a CSV file
   */
  async importWorkflows(
    divisionId: string,
    file: File,
    options: {
      updateExisting?: boolean;
      createUsers?: boolean;
      routingRuleField?: string;
      templateId?: string;
      applyTemplateAttributes?: boolean;
    } = {}
  ): Promise<WorkflowImportResult> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('updateExisting', String(options.updateExisting ?? false));
    formData.append('createUsers', String(options.createUsers ?? true));
    if (options.routingRuleField) {
      formData.append('routingRuleField', options.routingRuleField);
    }
    if (options.templateId) {
      formData.append('templateId', options.templateId);
      formData.append('applyTemplateAttributes', String(options.applyTemplateAttributes ?? false));
    }

    return this.httpClient.post<WorkflowImportResult>(
      `/api/divisions/${divisionId}/workflows/import`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    );
  }
}

// Workflow Import Types
export interface WorkflowImportResult {
  summary: WorkflowImportSummary;
  errors: WorkflowImportError[];
  workflows: WorkflowImportedWorkflow[];
}

export interface WorkflowImportSummary {
  totalRecords: number;
  workflowsCreated: number;
  workflowsUpdated: number;
  stepsCreated: number;
  usersCreated: number;
  userRolesAssigned: number;
  errorCount: number;
  skippedEmptyRows: number;
}

export interface WorkflowImportError {
  rowNumber: number;
  dodaacbeabesa?: string;
  roleName?: string;
  errorMessage: string;
}

export interface WorkflowImportedWorkflow {
  dodaacbeabesa: string;
  workflowId: string;
  stepsImported: number;
  usersLinked: number;
}
